import React from 'react';
import { useFormikContext } from 'formik';
import GeneralSettingsSpec from '../../../types/GeneralSettingsSpec';
import { FormsModule, PermissionsManager } from 'UI-Dash-Commons-UI';
import AccountSettingsOptionsSpec from '../../../types/Options/AccountSettingsOptionsSpec';
import AccountSettingsValidationUtils from '../../../utils/validators/AccountSettingsValidationUtils';
import styled from 'styled-components';

const CONTENT_PUBLISHER_ROLE_ID = 79;
const CONTENT_PUBLISHER_DEVELOPER_ROLE_ID = 82;
const SUB_ARRAY_USER_ROLE_ID = 103;
const SUB_WURL_USER_ROLE_ID = 109;
const ECOMMERCE_ACCOUNT_ROLE_ID = 112;
const SUB_ECOMM_ACCOUNT = 120;
const SUB_MASTER_ECOMMERCE_ACCOUNT_ROLE_ID = 118;
const MASTER_ECOMM_ACCOUNT = 115;
const ECOMM_USER_ROLES = [ ECOMMERCE_ACCOUNT_ROLE_ID, SUB_ECOMM_ACCOUNT, SUB_MASTER_ECOMMERCE_ACCOUNT_ROLE_ID, MASTER_ECOMM_ACCOUNT ];

export const isEcommUser = ( roleId: number ) => ECOMM_USER_ROLES.includes( roleId );

type AccountSettingsComponentProps =
    {
        accountSettingsOptions: AccountSettingsOptionsSpec | null,
    };

const AccountSettingsComponent: React.FunctionComponent<AccountSettingsComponentProps> =
    (
        {
            accountSettingsOptions,
        }
    ) =>
    {
        const { values } = useFormikContext<GeneralSettingsSpec>();
        const masterEcommerceAccounts = accountSettingsOptions ? accountSettingsOptions.masterEcommerceAccounts.map( option => ( {value: option.val, displayValue: option.dval} ))  : [];

        if (values.accountSettingsSpec.accountRoleId == ECOMMERCE_ACCOUNT_ROLE_ID)
        {
            masterEcommerceAccounts.unshift( {value: '0', displayValue: '- Optional -'} );
        }

        return (
            <>
                {
                    values.accountSettingsSpec &&
                    <>
                        {
                            values.accountSettingsSpec.accountRoleId !== null &&
                            accountSettingsOptions &&
                            <FormsModule.FormInputSelectField
                                name={'accountSettingsSpec.accountRoleId'}
                                elements={accountSettingsOptions.roles.map( option => ( {value: option.val, displayValue: option.dval} ) )}
                                label={'Account Type'}
                                hasSectionHeaderElements={true}
                            />
                        }
                        {
                            PermissionsManager.hasOneDirectPermission( 'edit_user_any' ) &&
                            values.accountSettingsSpec.accountRoleId == CONTENT_PUBLISHER_ROLE_ID &&
                            <StyledCheckboxContainer>
                                <FormsModule.FormCheckbox
                                    name={`accountSettingsSpec.extras[isExternal]`}
                                    label={'This MPA Account is intended for external use'}
                                />
                            </StyledCheckboxContainer>
                        }
                        {
                            accountSettingsOptions &&
                            Number( values.accountSettingsSpec.accountRoleId ) == CONTENT_PUBLISHER_DEVELOPER_ROLE_ID &&
                            <FormsModule.FormInputSelectField
                                name={'accountSettingsSpec.parentId'}
                                elements={accountSettingsOptions.contentPublishers.map( option => ( {value: option.val, displayValue: option.dval} ) )}
                                label={'Master Publishing Account'}
                                placeholder={'- Select One -'}
                                validate={( value ) => AccountSettingsValidationUtils.validateParentId( value )}
                            />
                        }
                        {
                            accountSettingsOptions &&
                            Number( values.accountSettingsSpec.accountRoleId ) == SUB_ARRAY_USER_ROLE_ID &&
                            <FormsModule.FormInputSelectField
                                name={'accountSettingsSpec.parentId'}
                                elements={accountSettingsOptions.arrayUsers.map( option => ( {value: option.val, displayValue: option.dval} ) )}
                                label={'Array Account'}
                                placeholder={'- Select One -'}
                                validate={( value ) => AccountSettingsValidationUtils.validateParentId( value )}
                            />
                        }
                        {
                            accountSettingsOptions &&
                            Number( values.accountSettingsSpec.accountRoleId ) == SUB_WURL_USER_ROLE_ID &&
                            <FormsModule.FormInputSelectField
                                name={'accountSettingsSpec.parentId'}
                                elements={accountSettingsOptions.wurlUsers.map( option => ( {value: option.val, displayValue: option.dval} ) )}
                                label={'Wurl Account'}
                                placeholder={'- Select One -'}
                                validate={( value ) => AccountSettingsValidationUtils.validateParentId( value )}
                            />
                        }
                        {
                            // Ecommerce Account can optionally be assigned to a Master Ecommerce Account
                            accountSettingsOptions &&
                            Number( values.accountSettingsSpec.accountRoleId ) == ECOMMERCE_ACCOUNT_ROLE_ID &&
                            <FormsModule.FormInputSelectField
                                name={'accountSettingsSpec.parentId'}
                                elements={masterEcommerceAccounts}
                                label={'Master Ecommerce Account'}
                                placeholder={'- Optional -'}
                            />
                        }
                        {
                            accountSettingsOptions &&
                            Number( values.accountSettingsSpec.accountRoleId ) == SUB_MASTER_ECOMMERCE_ACCOUNT_ROLE_ID &&
                            <FormsModule.FormInputSelectField
                                name={'accountSettingsSpec.parentId'}
                                elements={masterEcommerceAccounts}
                                label={'Master Ecommerce Account'}
                                placeholder={'- Select One -'}
                                validate={( value ) => AccountSettingsValidationUtils.validateParentId( value )}
                            />
                        }
                        {
                            values.accountSettingsSpec.parentEmail !== null &&
                            <FormsModule.FormInputField
                                name={'accountSettingsSpec.parentEmail'}
                                label={'Parent Account'}
                                isDisabled={true}
                            />
                        }
                        {
                            values.accountSettingsSpec.companyName !== null &&
                            <FormsModule.FormInputField
                                name={'accountSettingsSpec.companyName'}
                                label={'Company'}
                            />
                        }
                        {
                            values.accountSettingsSpec.firstName !== null &&
                            <FormsModule.FormInputField
                                name={'accountSettingsSpec.firstName'}
                                label={'First Name'}
                                validate={( value ) => AccountSettingsValidationUtils.validateName( value )}
                            />
                        }
                        {
                            values.accountSettingsSpec.lastName !== null &&
                            <FormsModule.FormInputField
                                name={'accountSettingsSpec.lastName'}
                                label={'Last Name'}
                                validate={( value ) => AccountSettingsValidationUtils.validateName( value )}
                            />
                        }
                        {
                            values.accountSettingsSpec.telephone !== null &&
                            <FormsModule.FormInputField
                                name={'accountSettingsSpec.telephone'}
                                label={'Telephone'}
                            />
                        }
                        <FormsModule.FormInputField
                            name={'accountSettingsSpec.email'}
                            label={'Email'}
                        />
                        {
                            values.accountSettingsSpec.website !== null &&
                            <FormsModule.FormInputField
                                name={'accountSettingsSpec.website'}
                                label={'Website'}
                                validate={( value ) => AccountSettingsValidationUtils.validateWebsite( value )}
                            />
                        }
                    </>
                }
            </>
        );
    }

const StyledCheckboxContainer = styled.div`
    margin-bottom: 10px;
`;

export default AccountSettingsComponent;
